import { useCallback, useMemo } from 'react';

import { Accordion } from '@/components/Accordion';
import { AnnouncementBar } from '@/components/AnnouncementBar';
import { HeaderSubPage } from '@/components/HeaderSubPage';
import { CheckIcon, CloseIcon } from '@/components/Icons';
import { Navbar } from '@/components/Navbar';
import { OverviewCard } from '@/components/OverviewCard';
import { Spacer } from '@/components/Spacer';
import { Tag } from '@/components/Tag';
import { useItineraryContext } from '@/context/ItineraryContext';
import { EnforcePageWidth } from '@/layout/PageLayout';
import { getAmountOfDaysIncludingStartEnd } from '@/utils/getAmountOfDaysIncludingStartEnd';
import { getAmountOfNightsBetween } from '@/utils/getAmountOfNightsBetween';
import { getItineraryProductsOfType } from '@/utils/getItineraryProductsOfType';

import { FlightInfo } from './FlightInfo';
import { StyledDetails, StyledListItem } from './styles';

import type { ItineraryProduct } from '@/types/products';

export function OverviewPage() {
  const { data } = useItineraryContext();

  const selectedFlightPlans = useMemo(
    () => data?.flightPlans?.filter((flightPlan) => flightPlan.selected),
    [data?.flightPlans],
  );
  const isSelected = useCallback((product: ItineraryProduct): boolean => product.selected, []);

  const itineraryProducts = data?.ItineraryBlocks.map((block) => block.ItineraryProducts).flat() || [];
  const selectedAccommodations = getItineraryProductsOfType(itineraryProducts, 'accommodation').filter(isSelected);
  const selectedExcursions = getItineraryProductsOfType(itineraryProducts, 'excursion').filter(isSelected);
  const selectedCarRentals = getItineraryProductsOfType(itineraryProducts, 'carrental').filter(isSelected);

  return (
    <>
      <AnnouncementBar />
      <Navbar variant="secondary" />
      <HeaderSubPage title="Your itinerary" emptyHeader />

      <EnforcePageWidth>
        <Spacer size="lg" />
        <Tag>Step 1 of 3</Tag>
        <>
          <h2>Summary</h2>

          <OverviewCard />

          <Spacer size="2xl" />
          <StyledDetails>
            <div>
              <h3>Included</h3>
              <Accordion title="Flights" icon={<CheckIcon />}>
                {/* Show info about selected flightplan(s?) */}
                {selectedFlightPlans?.map((flightPlan) =>
                  flightPlan.flights.map((flight) => (
                    <FlightInfo
                      key={flight.id}
                      flight={flight}
                      flightPlan={flightPlan}
                      amountOfTravelers={data.amountOfTravelers}
                    />
                  )),
                )}
                <StyledListItem>Meet & assist at airport on arrival</StyledListItem>
                <StyledListItem>Airport transfer</StyledListItem>
                <StyledListItem>CO2 offsetting flights for sustainable CO2ZERO projects</StyledListItem>
              </Accordion>
              <Accordion title="Accommodations" icon={<CheckIcon />}>
                <ul>
                  {/* Show info about selected accommodations */}
                  {selectedAccommodations.map((accommodation) => (
                    <StyledListItem key={accommodation.id}>
                      {getAmountOfNightsBetween(accommodation.startDate, accommodation.endDate)}
                      nights stay in {accommodation.Supplier.name}
                    </StyledListItem>
                  ))}
                  <StyledListItem>All taxes and surcharges</StyledListItem>
                  <StyledListItem>All meals mentioned</StyledListItem>
                </ul>
              </Accordion>

              <Accordion title="Activities" icon={<CheckIcon />}>
                <ul>
                  {/* Show info about selected accommodations */}
                  {selectedExcursions.map((excursion) => (
                    // Q: Wat is de + € 250,- in het design en wanneer moet dit getoond worden?
                    <StyledListItem key={excursion.id}>{excursion.name} + € ???,- </StyledListItem>
                  ))}
                </ul>
              </Accordion>

              <Accordion title="Transport" icon={<CheckIcon />}>
                <ul>
                  {selectedCarRentals.map((car) => (
                    <StyledListItem key={car.id}>
                      {getAmountOfDaysIncludingStartEnd(car.startDate, car.endDate)} days available {`${car.name}`} or
                      equivalent
                      {/* Weird {`${car.name}`} because eslint will complain about max-length
                        and using a newline will remove the ' ' space */}
                    </StyledListItem>
                  ))}
                  <StyledListItem>All-risk car insurance</StyledListItem>
                </ul>
              </Accordion>

              <Accordion title="Service & Conditions" icon={<CheckIcon />}>
                <ul>
                  <StyledListItem>Visa ???</StyledListItem>
                  <StyledListItem>Personalised travel app with information, inspiration and tips</StyledListItem>
                  <StyledListItem>Emergency helpline number (available 24/7)</StyledListItem>
                  <StyledListItem>Mandatory contribution to calamity fund</StyledListItem>
                  <StyledListItem>Compulsory contribution to SGR</StyledListItem>
                </ul>
              </Accordion>
            </div>
            <div>
              <h3>Not included</h3>
              <Accordion title="Travel insurance" icon={<CloseIcon />}>
                Travel insurance is not included.
              </Accordion>
              <Accordion title="Lunch and dinner" icon={<CloseIcon />}>
                Lunch and dinner are not included.
              </Accordion>
            </div>
          </StyledDetails>
        </>

        <Spacer size="2xl" />
      </EnforcePageWidth>
    </>
  );
}
